import { useEffect, useRef, useState } from "react";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";
import ServiceStep from "./ServiceStep";
import { useLanguage } from "../context/LanguageContext";
import useInView from "../hooks/useInView";
import useMediaQuery from "../hooks/useMediaQuery";

const AUTOPLAY_INTERVAL_MS = 2800;
const SCROLL_DURATION_MS = 700;

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

// A hand-rolled, fixed-duration scroll animation instead of the native
// `scrollTo({ behavior: "smooth" })` — the native version's speed scales
// with distance, so the short hop between adjacent cards and the long
// wrap-around hop from the last card back to the first play at very
// different speeds and the loop reads as a jarring flick rather than a
// consistent, smooth transition.
function animateScrollLeft(
  el: HTMLElement,
  target: number,
  duration: number,
  onComplete: () => void,
): () => void {
  const start = el.scrollLeft;
  const change = target - start;
  const startTime = performance.now();
  let frame: number;

  const step = (now: number) => {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    el.scrollLeft = start + change * easeInOutCubic(progress);
    if (progress < 1) {
      frame = requestAnimationFrame(step);
    } else {
      onComplete();
    }
  };
  frame = requestAnimationFrame(step);
  return () => cancelAnimationFrame(frame);
}

export default function Services({ className = "" }: { className?: string }) {
  const { t } = useLanguage();
  const { ref: sectionRef, inView: hasEnteredView } = useInView<HTMLElement>(0.3);
  // Autoplay is desktop-only: on mobile the row is a plain swipe-to-scroll
  // carousel the visitor fully controls, with nothing sliding on its own.
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const trackRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const autoplayTimer = useRef<number>();
  const autoplayPaused = useRef(false);
  const cancelScroll = useRef<() => void>();
  // True for the duration of any JS-driven scrollToCard animation (autoplay
  // tick or click). The scroll listener below must ignore scroll events
  // while this is set — otherwise, mid-animation, it independently
  // recomputes "closest card to center" from live geometry and can
  // overwrite the intended target with whatever card the track is passing
  // by at that instant. That's most likely to bite on the long wrap-around
  // scroll (last card back to first), which sweeps past every other card's
  // center on the way — a single mid-flight misfire there sets activeIndex
  // to the wrong card, and since every later autoplay tick computes
  // `(i + 1) % steps.length` off whatever activeIndex currently is, the
  // whole cycle stays permanently offset by one from then on (autoplay
  // "restarting" on card 2 instead of card 1, forever, not just once).
  const isProgrammaticScroll = useRef(false);

  const steps = [
    { title: t("service1Title"), description: t("service1Desc") },
    { title: t("service2Title"), description: t("service2Desc") },
    { title: t("service3Title"), description: t("service3Desc") },
    { title: t("service4Title"), description: t("service4Desc") },
    { title: t("service5Title"), description: t("service5Desc") },
  ];

  // `activeIndex` is the single source of truth, starting on the first card.
  // On touch devices, where the row genuinely scrolls, dragging refines it to
  // whichever card sits closest to the track's center — but at desktop
  // widths all 5 cards barely overflow the track at all, so there's almost
  // no scrollable distance to work with; deriving "active" purely from
  // scroll position would leave it stuck wherever the layout happens to
  // center by default (and unable to move on click, since there's nowhere
  // left to scroll to). So clicks and autoplay below set this state
  // directly, and only an actual drag/scroll gesture is left to nudge it via
  // geometry.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame: number;

    const updateActive = () => {
      const trackRect = track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - trackCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      });
      setActiveIndex(closestIndex);
    };

    // Edge fades should only appear on whichever side still has a card cut
    // off — never on the first card's leading edge or the last card's
    // trailing edge, since there's nothing further to reveal there.
    const updateScrollBounds = () => {
      setCanScrollLeft(track.scrollLeft > 4);
      setCanScrollRight(track.scrollLeft < track.scrollWidth - track.clientWidth - 4);
    };

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!isProgrammaticScroll.current) updateActive();
        updateScrollBounds();
      });
    };

    updateScrollBounds();
    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [steps.length]);

  // Centers a card by scrolling the track itself directly, rather than
  // `card.scrollIntoView()` — that API also walks up to the page as the
  // "nearest" scrollable ancestor for the vertical axis and was yanking the
  // whole page down to the section on every autoplay tick, which is exactly
  // the "forced scroll" this avoids. Only the row's own horizontal scroll
  // position moves; the document's scroll position is never touched.
  const scrollToCard = (index: number) => {
    const track = trackRef.current;
    const card = cardRefs.current[index];
    if (!track || !card) return;
    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const cardCenter = cardRect.left - trackRect.left + track.scrollLeft + cardRect.width / 2;
    const targetScrollLeft = cardCenter - track.clientWidth / 2;
    cancelScroll.current?.();
    isProgrammaticScroll.current = true;
    cancelScroll.current = animateScrollLeft(track, targetScrollLeft, SCROLL_DURATION_MS, () => {
      isProgrammaticScroll.current = false;
    });
  };

  // Auto-advances the spotlighted step every few seconds, like the
  // reference design's rotating highlight — pauses while the visitor is
  // hovering, touching, or dragging the row so it never fights their input.
  // Advances the React state directly (not by recomputing scroll geometry)
  // so it keeps working even where there's no room left to scroll. Doesn't
  // start until the section has actually scrolled into view — otherwise it
  // ticks silently while off-screen and the visitor arrives mid-sequence
  // instead of on step one.
  useEffect(() => {
    if (!hasEnteredView || !isDesktop) return;
    const restartAutoplay = () => {
      window.clearInterval(autoplayTimer.current);
      autoplayTimer.current = window.setInterval(() => {
        if (autoplayPaused.current) return;
        setActiveIndex((i) => {
          const next = (i + 1) % steps.length;
          scrollToCard(next);
          return next;
        });
      }, AUTOPLAY_INTERVAL_MS);
    };

    restartAutoplay();
    return () => window.clearInterval(autoplayTimer.current);
  }, [steps.length, hasEnteredView, isDesktop]);

  // Applied on every breakpoint, not just desktop — on mobile a hard clip
  // left a flat grey slab of the next inactive (opacity-60) card sitting
  // right at the container edge and chopping off the active card's shadow;
  // fading both to transparent instead blends the peeking card into the
  // section background and lets the shadow taper off naturally.
  const maskImage = `linear-gradient(to right, ${
    canScrollLeft ? "transparent, black 4%" : "black"
  }, black ${canScrollRight ? "96%, transparent" : "100%"})`;

  const goTo = (index: number) => {
    setActiveIndex(index);
    scrollToCard(index);
  };

  return (
    <section
      id="services"
      ref={sectionRef}
      className={`bg-steel-100 pt-24 pb-24 md:pt-32 md:pb-32 ${className}`}
      onMouseEnter={() => {
        autoplayPaused.current = true;
      }}
      onMouseLeave={() => {
        autoplayPaused.current = false;
      }}
      onPointerDown={() => {
        autoplayPaused.current = true;
      }}
    >
      <div className="container-industrial">
        <Reveal>
          <SectionHeading title={t("servicesHeading")} />
        </Reveal>
      </div>

      {/* The scrollport's own overflow-x-auto is what clips box-shadow at
          its edges — a plain px-6 padding here would just push the first
          card inward from the site's margin (wrong: leaves a gap nothing
          else on the page has). Instead the track bleeds past the container
          via a negative margin that exactly matches container-industrial's
          own padding, then re-applies that same padding on itself — so the
          *clip boundary* moves out to the true viewport edge (room for the
          shadow to breathe) while the *card* still starts flush at the same
          margin as every other section, at any viewport width. The mask
          fades whatever card is only partially peeking at either edge,
          instead of hard-clipping it. */}
      <div className="container-industrial relative mt-8">
        <div
          ref={trackRef}
          className="no-scrollbar-mobile -mx-6 flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 pb-16 pt-8 md:-mx-10 md:px-10 xl:-mx-16 xl:px-16"
          style={{ WebkitMaskImage: maskImage, maskImage }}
        >
          {steps.map((step, i) => (
            <ServiceStep
              key={step.title}
              ref={(el) => (cardRefs.current[i] = el)}
              index={i}
              title={step.title}
              description={step.description}
              active={i === activeIndex}
              onSelect={() => goTo(i)}
            />
          ))}
        </div>

        <div className="h-0.5 bg-steel-300">
          <div
            className="h-full bg-accent transition-transform duration-300 ease-engineered md:duration-500 md:ease-out"
            style={{
              width: `${100 / steps.length}%`,
              transform: `translateX(${activeIndex * 100}%)`,
            }}
          />
        </div>
      </div>
    </section>
  );
}
